import React from 'react';

import { XMarkIcon } from '@heroicons/react/24/outline';
import FocusTrap from 'focus-trap-react';

import { Col, Row } from './Flex';
import { Transition } from './Transition';
import { SecurePortal } from './SecurePortal';
import { ActionIcon } from './ActionIcon';

import { useBodyScrollLock, useHandleEscKey } from 'domain/hooks';
import { createId, noop } from 'domain/utils';
import { SROnly } from './SROnly';
import { cn } from 'domain/core';

type IModalContext = {
  open: boolean;
  id: string;
  dismissable: boolean;
  onClose: VoidFunction;
};
const ModalContext = React.createContext<IModalContext>({
  open: false,
  id: createId('modal_'),
  dismissable: false,
  onClose: noop,
});

const useModalContext = () => React.useContext(ModalContext);

interface ModalProps extends React.PropsWithChildren {
  open: boolean;
  dismissable?: boolean;
  className?: string;
  focusTrapActive?: boolean;
  id?: string;
  onClose: VoidFunction;
}
/**
 * Modal wrapper component. Manages open and close
 * @param props
 * @returns
 */
const Modal = (props: ModalProps) => {
  const id = React.useRef(props.id ?? createId('modal_'));
  const locker = useBodyScrollLock();

  React.useEffect(() => {
    locker(props.open);
  }, [props.open]);

  const guardedClose = React.useCallback(() => {
    if (props.dismissable) {
      props.onClose();
    }
  }, [props.dismissable, props.onClose]);

  useHandleEscKey(props.open, guardedClose);

  const ctx = React.useMemo(
    () => ({
      id: id.current,
      open: props.open,
      dismissable: props.dismissable ?? false,
      onClose: guardedClose,
    }),
    [props.open, props.dismissable, guardedClose]
  );

  return (
    <ModalContext.Provider value={ctx}>
      <Transition.Fade in={props.open}>
        <FocusTrap active={props.open && props.focusTrapActive}>
          <Row
            className={cn(
              'modal-root',
              'fixed z-[1000] inset-0 h-screen w-screen',
              props.className
            )}
          >
            {props.children}
          </Row>
        </FocusTrap>
      </Transition.Fade>
    </ModalContext.Provider>
  );
};

/**
 * Modal overlay. Apply shading on the background
 * @param props
 * @returns
 */
const Overlay = (props: { className?: string }) => {
  const { onClose } = useModalContext();

  return (
    <div
      className={cn(
        'modal-overlay',
        'absolute z-10 w-full h-full bg-black/25',
        props.className
      )}
      onClick={onClose}
    />
  );
};

interface PanelProps extends React.PropsWithChildren {
  className?: string;
}
/**
 * Modal content container
 * @param props
 * @returns
 */
const Panel = (props: PanelProps) => {
  const { open, id } = useModalContext();

  return (
    <Transition.Appear in={open}>
      <Col
        role='dialog'
        aria-labelledby={`${id}_title`}
        aria-describedby={`${id}_description`}
        className={cn(
          'modal-panel',
          '!w-auto !h-auto p-4 z-20',
          'bg-neutral-50 rounded shadow-2xl',
          props.className
        )}
      >
        {props.children}
      </Col>
    </Transition.Appear>
  );
};

interface TitleProps extends React.PropsWithChildren {
  className?: string;
}
/**
 * Modal title
 * @param props
 * @returns
 */
const Title = (props: TitleProps) => {
  const { id, dismissable, onClose } = useModalContext();

  return (
    <Row
      className={cn('modal-title', 'justify-between', props.className)}
      id={`${id}_title`}
    >
      {props.children}
      {dismissable && (
        <ActionIcon onClick={onClose}>
          <XMarkIcon
            className={cn(
              'modal-close-icon',
              'w-6 h-6 text-neutral-700 cursor-pointer'
            )}
          />
        </ActionIcon>
      )}
    </Row>
  );
};
interface DescriptionProps extends React.PropsWithChildren {
  className?: string;
}
/**
 * Modal description. Not shown, only used to describe the modal in an accessible way.
 * @param props
 * @returns
 */
const Description = (props: DescriptionProps) => {
  const { id } = useModalContext();

  return (
    <SROnly className={props.className} id={`${id}_description`}>
      {props.children}
    </SROnly>
  );
};

interface BodyProps extends React.PropsWithChildren {
  className?: string;
}
/**
 * Modal content body
 * @param props
 * @returns
 */
const Body = (props: BodyProps) => {
  return (
    <Col className={cn('modal-body', props.className)}>{props.children}</Col>
  );
};

interface FooterProps extends React.PropsWithChildren {
  className?: string;
}
/**
 * Modal content footer
 * @param props
 * @returns
 */
const Footer = (props: FooterProps) => {
  return (
    <Row className={cn('modal-footer', props.className)}>{props.children}</Row>
  );
};

interface PortalProps extends React.PropsWithChildren {}
/**
 * If the modal is rendered as a child of this component, it will be rendered inside the portal created by AppShell.ModalContainer
 * @param props
 * @returns
 */
const Portal = (props: PortalProps) => {
  return (
    <SecurePortal id='app-shell-modal-portal'>{props.children}</SecurePortal>
  );
};

Modal.Overlay = Overlay;
Modal.Panel = Panel;
Modal.Title = Title;
Modal.Description = Description;
Modal.Body = Body;
Modal.Footer = Footer;
Modal.Portal = Portal;

export { Modal };
